/**
 * Production Error Handling Hook
 * 
 * Comprehensive error handling with:
 * - Automatic retry mechanisms
 * - Error classification and routing
 * - User-friendly error messages
 * - Error reporting and monitoring
 * - Recovery suggestions
 */

import React, { useCallback, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconRefresh, IconAlertCircle } from '@tabler/icons-react';
import { RetryMechanism, RetryConfigs, CircuitBreakerConfigs, createRetryMechanism } from './RetryMechanism';

export interface ErrorDetails {
  id: string;
  type: ErrorType;
  message: string;
  originalError: unknown;
  timestamp: Date;
  context?: Record<string, unknown>;
  recoverable: boolean;
  userMessage: string;
  technicalMessage: string;
  suggestedActions: string[];
  severity?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'EMERGENCY';
  component?: string;
}

export type ErrorType = 
  | 'network'
  | 'validation'
  | 'authentication'
  | 'authorization'
  | 'business'
  | 'system'
  | 'timeout'
  | 'unknown';

export interface ErrorHandlingConfig {
  enableRetry?: boolean;
  enableNotifications?: boolean;
  enableReporting?: boolean;
  maxRetries?: number;
  retryDelay?: number;
  showTechnicalDetails?: boolean;
}

export interface UseErrorHandlingResult {
  // Error state
  errors: ErrorDetails[];
  hasErrors: boolean;
  lastError: ErrorDetails | null;
  
  // Error handling functions
  handleError: (error: unknown, context?: Record<string, unknown>) => ErrorDetails;
  clearError: (errorId: string) => void;
  clearAllErrors: () => void;
  
  // Retry mechanisms
  retryOperation: <T>(operation: () => Promise<T>, config?: Partial<ErrorHandlingConfig>) => Promise<T>;
  
  // Utility functions
  isRecoverable: (error: ErrorDetails) => boolean;
  getRecoveryActions: (error: ErrorDetails) => string[];
  
  // Metrics
  getErrorMetrics: () => {
    totalErrors: number;
    errorsByType: Record<ErrorType, number>;
    recoverableErrors: number;
    criticalErrors: number;
  };
}

const DEFAULT_CONFIG: ErrorHandlingConfig = {
  enableRetry: true,
  enableNotifications: true,
  enableReporting: true,
  maxRetries: 3,
  retryDelay: 1000,
  showTechnicalDetails: false
};

export const useErrorHandling = (config: ErrorHandlingConfig = {}): UseErrorHandlingResult => {
  const [errors, setErrors] = useState<ErrorDetails[]>([]);
  const configRef = useRef({ ...DEFAULT_CONFIG, ...config });
  const retryMechanismRef = useRef<RetryMechanism | null>(null);

  // Initialize retry mechanism
  if (!retryMechanismRef.current) {
    retryMechanismRef.current = createRetryMechanism(
      {
        ...RetryConfigs.STANDARD,
        maxAttempts: configRef.current.maxRetries || 3,
        onRetry: (attempt, error) => {
          if (configRef.current.enableNotifications) {
            notifications.show({
              title: 'Retrying Operation',
              message: `Attempt ${attempt} of ${configRef.current.maxRetries}`,
              color: 'blue',
              icon: <IconRefresh />,
              autoClose: 2000
            });
          }
        }
      },
      CircuitBreakerConfigs.STANDARD
    );
  }

  const classifyError = useCallback((error: unknown): ErrorType => {
    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      
      // Network errors
      if (message.includes('network') || 
          message.includes('fetch') || 
          message.includes('connection') ||
          message.includes('502') ||
          message.includes('503') ||
          message.includes('504')) {
        return 'network';
      }
      
      // Timeout errors
      if (message.includes('timeout') || message.includes('408')) {
        return 'timeout';
      }
      
      // Authentication errors
      if (message.includes('401') || 
          message.includes('unauthorized') ||
          message.includes('authentication') ||
          message.includes('login') ||
          message.includes('token')) {
        return 'authentication';
      }
      
      // Authorization errors
      if (message.includes('403') || 
          message.includes('forbidden') ||
          message.includes('permission') ||
          message.includes('access denied')) {
        return 'authorization';
      }
      
      // Validation errors
      if (message.includes('400') ||
          message.includes('validation') ||
          message.includes('invalid') ||
          message.includes('required') ||
          message.includes('format')) {
        return 'validation';
      }
      
      // Business logic errors
      if (message.includes('business') ||
          message.includes('rule') ||
          message.includes('policy') ||
          message.includes('workflow')) {
        return 'business';
      }
      
      // System errors
      if (message.includes('500') ||
          message.includes('internal') ||
          message.includes('database') ||
          message.includes('server')) {
        return 'system';
      }
    }
    
    return 'unknown';
  }, []);

  const createErrorDetails = useCallback((
    error: unknown, 
    context?: Record<string, unknown>
  ): ErrorDetails => {
    const errorType = classifyError(error);
    const timestamp = new Date();
    const id = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    
    const errorMessage = error instanceof Error ? error.message : String(error);
    
    const errorDetails: ErrorDetails = {
      id,
      type: errorType,
      message: errorMessage,
      originalError: error,
      timestamp,
      context,
      recoverable: isErrorRecoverable(errorType),
      userMessage: getUserMessage(errorType, errorMessage),
      technicalMessage: getTechnicalMessage(error),
      suggestedActions: getSuggestedActions(errorType)
    };
    
    return errorDetails;
  }, [classifyError]);

  const handleError = useCallback((
    error: unknown, 
    context?: Record<string, unknown>
  ): ErrorDetails => {
    const errorDetails = createErrorDetails(error, context);
    
    // Add to error list
    setErrors(prev => [...prev, errorDetails]);
    
    // Show notification if enabled
    if (configRef.current.enableNotifications) {
      showErrorNotification(errorDetails);
    }
    
    // Report error if enabled
    if (configRef.current.enableReporting) {
      reportError(errorDetails);
    }
    
    // Log error
    console.error('Error handled:', {
      ...errorDetails,
      stack: error instanceof Error ? error.stack : undefined
    });
    
    return errorDetails;
  }, [createErrorDetails]);

  const clearError = useCallback((errorId: string) => {
    setErrors(prev => prev.filter(error => error.id !== errorId));
    notifications.hide(errorId);
  }, []);

  const clearAllErrors = useCallback(() => {
    errors.forEach(error => notifications.hide(error.id));
    setErrors([]);
  }, [errors]);

  const retryOperation = useCallback(async <T,>(
    operation: () => Promise<T>,
    operationConfig?: Partial<ErrorHandlingConfig>
  ): Promise<T> => {
    if (!configRef.current.enableRetry || !retryMechanismRef.current) {
      return operation();
    }
    
    try {
      return await retryMechanismRef.current.execute(operation);
    } catch (error) {
      const errorDetails = handleError(error, { retryAttempted: true });
      throw errorDetails;
    }
  }, [handleError]);

  const isRecoverable = useCallback((error: ErrorDetails): boolean => {
    return error.recoverable;
  }, []);

  const getRecoveryActions = useCallback((error: ErrorDetails): string[] => {
    return error.suggestedActions;
  }, []);

  const getErrorMetrics = useCallback(() => {
    const totalErrors = errors.length;
    const errorsByType = errors.reduce((acc, error) => {
      acc[error.type] = (acc[error.type] || 0) + 1;
      return acc;
    }, {} as Record<ErrorType, number>);
    
    const recoverableErrors = errors.filter(e => e.recoverable).length;
    const criticalErrors = errors.filter(e => 
      e.type === 'system' || e.type === 'authentication'
    ).length;
    
    return {
      totalErrors,
      errorsByType,
      recoverableErrors,
      criticalErrors
    };
  }, [errors]);

  const lastError = errors.length > 0 ? (errors[errors.length - 1] ?? null) : null;
  
  return {
    errors,
    hasErrors: errors.length > 0,
    lastError,
    handleError,
    clearError,
    clearAllErrors,
    retryOperation,
    isRecoverable,
    getRecoveryActions,
    getErrorMetrics
  };
};

// Helper functions

const isErrorRecoverable = (errorType: ErrorType): boolean => {
  const recoverableTypes: ErrorType[] = ['network', 'timeout', 'system'];
  return recoverableTypes.includes(errorType);
};

const getUserMessage = (errorType: ErrorType, originalMessage: string): string => {
  const messages = {
    network: 'Connection problem. Please check your internet connection and try again.',
    timeout: 'The operation took too long. Please try again.',
    authentication: 'Your session has expired. Please log in again.',
    authorization: 'You don\'t have permission to perform this action.',
    validation: 'Please check your input and correct any errors.',
    business: 'This action cannot be completed due to business rules.',
    system: 'A system error occurred. Our team has been notified.',
    unknown: 'An unexpected error occurred. Please try again.'
  };
  
  return messages[errorType] || messages.unknown;
};

const getTechnicalMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  return String(error);
};

const getSuggestedActions = (errorType: ErrorType): string[] => {
  const actions = {
    network: [
      'Check your internet connection',
      'Try refreshing the page',
      'Contact support if the problem persists'
    ],
    timeout: [
      'Try the operation again',
      'Check your connection speed',
      'Break large operations into smaller parts'
    ],
    authentication: [
      'Log in again',
      'Clear browser cache and cookies',
      'Contact administrator if problem persists'
    ],
    authorization: [
      'Contact your administrator',
      'Request necessary permissions',
      'Switch to an account with appropriate access'
    ],
    validation: [
      'Review and correct input data',
      'Check required fields',
      'Ensure data meets format requirements'
    ],
    business: [
      'Review business rules and policies',
      'Contact supervisor for guidance',
      'Check workflow requirements'
    ],
    system: [
      'Try again in a few minutes',
      'Contact technical support',
      'Check system status page'
    ],
    unknown: [
      'Try refreshing the page',
      'Log out and log back in',
      'Contact support with error details'
    ]
  };
  
  return actions[errorType] || actions.unknown;
};

const showErrorNotification = (errorDetails: ErrorDetails): void => {
  const colors = {
    network: 'orange',
    timeout: 'orange',
    authentication: 'red',
    authorization: 'red',
    validation: 'yellow',
    business: 'blue',
    system: 'red',
    unknown: 'gray'
  };
  
  notifications.show({
    id: errorDetails.id,
    title: `${errorDetails.type.charAt(0).toUpperCase() + errorDetails.type.slice(1)} Error`,
    message: errorDetails.userMessage,
    color: colors[errorDetails.type],
    icon: <IconAlertCircle />,
    autoClose: errorDetails.type === 'validation' ? 5000 : false,
    withCloseButton: true
  });
};

const reportError = async (errorDetails: ErrorDetails): Promise<void> => {
  try {
    // ✅ REPOSITORY PATTERN: Use ErrorReportingRepository instead of direct API calls
    const { ErrorReportingRepository, MockErrorReportingRepository } = await import('../data/repositories/ErrorReportingRepository');

    // ✅ SAFETY AUDIT FIX: Production safety checks to prevent mock usage
    const isDevelopment = process.env.NODE_ENV === 'development';
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    // CRITICAL: Ensure mock repositories are never used in production
    const errorRepo = (isDevelopment || isLocalhost) ? new MockErrorReportingRepository() : new ErrorReportingRepository();

    if (isDevelopment || isLocalhost) {
      console.log('[AUDIT] Using mock ErrorReportingRepository for development');
    } else {
      console.log('[AUDIT] Using production ErrorReportingRepository for error reporting');
    }

    // Create standardized error report using LifeCriticalError interface
    const lifeCriticalError = {
      id: errorDetails.id,
      type: mapErrorTypeToLifeCritical(errorDetails.type),
      severity: errorDetails.severity || 'MEDIUM',
      message: errorDetails.userMessage,
      stack: errorDetails.originalError instanceof Error ? errorDetails.originalError.stack : undefined,
      context: errorDetails.context || {},
      timestamp: errorDetails.timestamp.getTime(),
      tenantId: getCurrentTenantId(),
      userId: getCurrentUserId(),
      sessionId: getSessionId(),
      environment: process.env.NODE_ENV || 'production',
    };

    // Report through repository pattern
    await errorRepo.reportLifeCriticalError(lifeCriticalError);
  } catch {
    // Silently handle error reporting failures to prevent recursive errors
  }
};

const getCurrentUserId = (): string => {
  try {
    const user = JSON.parse(localStorage.getItem('auth_user') || '{}');
    return user.id || 'anonymous';
  } catch {
    return 'anonymous';
  }
};

const getCurrentTenantId = (): string => {
  try {
    const user = JSON.parse(localStorage.getItem('auth_user') || '{}');
    return user.tenantId || 'unknown';
  } catch {
    return 'unknown';
  }
};

const getSessionId = (): string => {
  try {
    return sessionStorage.getItem('sessionId') || `session_${Date.now()}`;
  } catch {
    return `session_${Date.now()}`;
  }
};

const mapErrorTypeToLifeCritical = (errorType: ErrorType): 'PATIENT_SAFETY' | 'FINANCIAL_INTEGRITY' | 'LEGAL_PRIVILEGE' | 'SYSTEM_CRITICAL' | 'OPERATIONAL' => {
  // Map ErrorType to LifeCriticalError type
  switch (errorType) {
    case 'authentication':
    case 'authorization':
      return 'SYSTEM_CRITICAL';
    case 'network':
    case 'timeout':
      return 'OPERATIONAL';
    case 'validation':
    case 'business':
      return 'OPERATIONAL';
    case 'system':
    case 'unknown':
    default:
      return 'OPERATIONAL';
  }
};